import {useRef, useState} from 'react';
import {Link, Navigate, useNavigate} from 'react-router';
import {useAuth} from '../hooks/AuthContext';
import {clearSession} from '../auth/legacy';
import {MESSAGES, SIGNUP_FIELDS, signup, validateSignup} from '../auth/signup';
import {providerErrorMessage} from '../auth/social';
import AuthLayout from '../components/auth/AuthLayout';
import PasswordField from '../components/auth/PasswordField';
import SsoButtons from '../components/auth/SsoButtons';
import TermsAgreement from '../components/auth/TermsAgreement';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';
import './Signup.css';

const ERROR_ID = 'auth-error';
const EMPTY = {first_name: '', last_name: '', email: '', phone: '', password: '', confirm_password: ''};
/** Form field -> DOM id / data-testid. */
const IDS = {
  first_name: 'signup-first-name',
  last_name: 'signup-last-name',
  email: 'signup-email',
  phone: 'signup-phone',
  password: 'signup-password',
  confirm_password: 'signup-confirm-password',
};
const errorId = (field) => `${IDS[field]}-error`;

/**
 * Legacy AIDentalBook sign-up page. Creates the account with POST /api/v1/auth/signup, which
 * returns the same session as a password sign-in, then lands in the app like a sign-in does.
 * Google / Apple / Facebook sign-up is the same provider-SDK flow as on /login.
 */
export default function Signup() {
  const auth = useAuth();
  const navigate = useNavigate();
  const keys = useIdempotencyKeys();
  const [values, setValues] = useState(EMPTY);
  const [agreed, setAgreed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const termsRef = useRef(null);
  const formRef = useRef(null);

  if (auth.user) return <Navigate to="/" replace />;

  const focusField = (field) => {
    const el =
      field === 'accept_terms'
        ? termsRef.current
        : formRef.current && formRef.current.querySelector(`#${IDS[field]}`);
    if (el) el.focus();
  };

  const showErrors = (errors, summary) => {
    setFieldErrors(errors);
    setError(summary || Object.values(errors).join('\n'));
    const first = [...SIGNUP_FIELDS, 'accept_terms'].find((f) => errors[f]);
    if (first) focusField(first);
  };

  const onChange = (event) => {
    const {name, value} = event.target;
    setValues((prev) => ({...prev, [name]: value}));
  };

  const onAgree = (checked) => {
    setAgreed(checked);
    if (checked && fieldErrors.accept_terms) {
      const rest = {...fieldErrors};
      delete rest.accept_terms;
      setFieldErrors(rest);
      setError(Object.values(rest).join('\n'));
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const errors = validateSignup({...values, accept_terms: agreed});
    if (Object.keys(errors).length) {
      showErrors(errors);
      return;
    }
    setFieldErrors({});
    setError('');
    setBusy(true);
    try {
      await keys.run('signup', values, (idempotencyKey) => signup(values, {idempotencyKey}));
    } catch (err) {
      setBusy(false);
      showErrors(err.fieldErrors || {}, err.message || MESSAGES.generic);
      return;
    }
    // Same as a password sign-in: the stored session loads the profile, then <Navigate> runs.
    const profile = await auth.getAccountData();
    if (!profile) {
      clearSession();
      setBusy(false);
      showErrors({}, MESSAGES.generic);
    }
  };

  const onSsoLocked = () => showErrors({accept_terms: MESSAGES.terms});

  /** Provider token -> session; on success `auth.user` is set and <Navigate> takes over. */
  const onSocialToken = async (provider, payload) => {
    if (busy) return;
    setFieldErrors({});
    setError('');
    setBusy(true);
    try {
      await auth.loginWithSocial({provider, ...payload});
    } catch (err) {
      setBusy(false);
      showErrors({}, err.message);
    }
  };

  const onProviderError = (provider, kind) => {
    setFieldErrors({});
    setError(providerErrorMessage(provider, kind));
  };

  const fieldProps = (field) => ({
    id: IDS[field],
    name: field,
    value: values[field],
    onChange,
    className: 'auth-input',
    'aria-invalid': fieldErrors[field] ? 'true' : undefined,
    'aria-describedby': fieldErrors[field] ? errorId(field) : undefined,
    'data-testid': IDS[field],
  });

  const fieldError = (field) =>
    fieldErrors[field] ? (
      <p id={errorId(field)} className="auth-field-error">
        {fieldErrors[field]}
      </p>
    ) : null;

  const termsInvalid = Boolean(fieldErrors.accept_terms);

  return (
    <AuthLayout>
      <button type="button" className="auth-back" aria-label="Go back" onClick={() => navigate(-1)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
          <path
            d="M15 5l-7 7 7 7"
            stroke="#C43B71"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="auth-heading auth-heading-tight">
        <p className="auth-eyebrow">GET STARTED</p>
        <h1 className="auth-title">Create your account.</h1>
        <p className="auth-subtitle">Your records, X-rays, and appointments — secure and always with you.</p>
      </div>

      <div className="auth-panel">
        <form ref={formRef} onSubmit={submit} noValidate aria-label="Create account">
          <div className="auth-fields">
            <div className="auth-name-row">
              <div className="auth-field">
                <label htmlFor={IDS.first_name} className="auth-label">
                  First name
                </label>
                <input
                  {...fieldProps('first_name')}
                  type="text"
                  placeholder="Jane"
                  autoComplete="given-name"
                  required
                />
                {fieldError('first_name')}
              </div>
              <div className="auth-field">
                <label htmlFor={IDS.last_name} className="auth-label">
                  Last name
                </label>
                <input
                  {...fieldProps('last_name')}
                  type="text"
                  placeholder="Chen"
                  autoComplete="family-name"
                  required
                />
                {fieldError('last_name')}
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor={IDS.email} className="auth-label">
                Email Address
              </label>
              <input
                {...fieldProps('email')}
                type="email"
                placeholder="name@example.com"
                autoComplete="email"
                required
              />
              {fieldError('email')}
            </div>

            <div className="auth-field">
              <label htmlFor={IDS.phone} className="auth-label">
                Phone <span className="auth-optional">(optional)</span>
              </label>
              <input {...fieldProps('phone')} type="tel" placeholder="+1 555 010 1234" autoComplete="tel" />
              {fieldError('phone')}
            </div>

            <div>
              <PasswordField
                {...fieldProps('password')}
                aria-describedby={['signup-password-hint', fieldErrors.password && errorId('password')]
                  .filter(Boolean)
                  .join(' ')}
                label="Password"
                toggleLabel="password"
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />
              <p id="signup-password-hint" className="auth-hint">
                At least 8 characters, with a letter and a number.
              </p>
              {fieldError('password')}
            </div>

            <div>
              <PasswordField
                {...fieldProps('confirm_password')}
                label="Confirm Password"
                toggleLabel="confirm password"
                placeholder="••••••••"
                autoComplete="new-password"
                required
              />
              {fieldError('confirm_password')}
            </div>
          </div>

          <TermsAgreement
            ref={termsRef}
            id="signup-terms"
            checked={agreed}
            onChange={onAgree}
            invalid={termsInvalid}
            describedBy={termsInvalid ? ERROR_ID : undefined}
          />

          <div id={ERROR_ID} className="auth-alert" role="alert" aria-atomic="true" data-testid="auth-error">
            {error}
          </div>

          <button type="submit" className="auth-submit" disabled={busy} data-testid="signup-submit">
            {busy ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <p className="auth-or">or sign up with</p>
        <SsoButtons
          verb="Sign up"
          testIdPrefix="signup-sso"
          locked={!agreed}
          busy={busy}
          describedBy={termsInvalid ? ERROR_ID : undefined}
          onLocked={onSsoLocked}
          onToken={onSocialToken}
          onProviderError={onProviderError}
        />

        <p className="auth-switch">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
